import { Drawer } from "@peer-start/ui";
import { Button } from "@peer-start/ui/reducate";
import { CircleCheck } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import badgeCheckInverse from "../../../assets/starting-teacher/badge-check-inverse.svg?url";
import { allStartingTeacherTasksAchieved } from "../data/starting-teacher-progress.js";
import { startingTeacherSources } from "../data/starting-teacher-sources.js";
import {
  findCompetency,
  isActiveSkill,
  skillStatus,
  type SkillRef,
  type StartingTeacherProgress,
  type StartingTeacherTaskId,
} from "../data/starting-teacher-route.js";
import { StartingTeacherStatusPill } from "./StartingTeacherStatusPill.js";
import { StartingTeacherQuizBanner } from "./StartingTeacherQuizBanner.js";
import { StartingTeacherQuizModal } from "./StartingTeacherQuizModal.js";
import { StartingTeacherRecommended } from "./StartingTeacherRecommended.js";
import { StartingTeacherRouteFrame } from "./StartingTeacherRouteFrame.js";
import { StartingTeacherSkillBadge } from "./StartingTeacherSticker.js";
import { StartingTeacherSources } from "./StartingTeacherSources.js";

export function StartingTeacherSkill({
  competencyId,
  skill,
  progress,
  topicId,
  aside,
  onComplete,
  onQuizResult,
  onAchieveTask,
  onSaveNote,
}: {
  competencyId: string;
  skill: SkillRef;
  progress: StartingTeacherProgress;
  topicId: string;
  aside: ReactNode;
  onSkipCourse: () => void;
  onComplete: (source: "self" | "quiz") => void;
  onQuizResult: (result: { percent: number; perfect: boolean }) => void;
  onAchieveTask: (taskId: StartingTeacherTaskId) => void;
  onSaveNote: (note: string) => void;
  onCloseTopic: () => void;
  onSelectTopic: (topicId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [quizOpen, setQuizOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [note, setNote] = useState(progress.topics[topicId]?.note ?? "");
  const competency = findCompetency(competencyId);
  const status = skillStatus(
    skill,
    progress,
    competency ? isActiveSkill(skill.id, competency, progress) : false,
  );
  const startedAt = progress.skillStartedAt[skill.id];
  const startedLabel = startedAt
    ? t("startingTeacher.startedOn", {
        date: new Intl.DateTimeFormat(i18n.language === "en" ? "en-GB" : "nl-NL", {
          day: "numeric",
          month: "long",
          year: "numeric",
        }).format(new Date(startedAt)),
      })
    : null;
  const completed = Boolean(progress.completedSkills[skill.id]);
  const allTasksAchieved = allStartingTeacherTasksAchieved(progress, skill.id);
  const checkedOff = progress.completedSkills[skill.id] === "self";
  const earned = allTasksAchieved || progress.completedSkills[skill.id] === "quiz";
  const headerAction = earned ? (
    <StartingTeacherSkillBadge label={t("startingTeacher.taskAchieved")} />
  ) : checkedOff ? (
    <Button
      className="!h-[32px] !min-h-[32px] !gap-[6px] !rounded-[10px] !border-[rgba(0,0,0,0.1)] !bg-[#62A000] !px-[12px] !py-0 !text-[14px] !leading-[20px] !font-medium !text-white !shadow-none hover:!bg-[#62A000] disabled:!border-[rgba(0,0,0,0.1)] disabled:!bg-[#62A000] disabled:!text-white disabled:!opacity-100"
      controlId="starting-teacher.progress.complete"
      disabled
      type="button"
      variant="secondary"
    >
      <img alt="" aria-hidden="true" height={16} src={badgeCheckInverse} width={16} />
      {t("startingTeacher.skillCheckedOff")}
    </Button>
  ) : completed ? undefined : (
    <Button
      className="[&_svg]:text-success-foreground"
      controlId="starting-teacher.progress.complete"
      onClick={() => onComplete("self")}
      type="button"
      variant="secondary"
    >
      <CircleCheck aria-hidden="true" className="size-4" strokeWidth={1.75} />
      {t("startingTeacher.markSkillComplete")}
    </Button>
  );

  return (
      <StartingTeacherRouteFrame
        aside={aside}
        asideAlign="content"
        controlId="starting-teacher.skill"
        headerAction={headerAction}
        header={
          <>
            <div className="flex flex-wrap items-center gap-2.5">
              <StartingTeacherStatusPill status={status} />
              {startedLabel ? <p className="text-xs font-medium text-muted-foreground">{startedLabel}</p> : null}
            </div>
            <h1 className="mt-2 text-[32px] font-medium leading-[48px] tracking-tight">
              {t(`startingTeacher.skills.${skill.id}.title`)}
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-5 text-secondary-text">
              {t(`startingTeacher.skills.${skill.id}.learn`)}
            </p>
          </>
        }
      >
        <div className="space-y-4">
          {!completed || progress.completedSkills[skill.id] === "quiz" ? (
            <StartingTeacherQuizBanner
              onOpen={() => setQuizOpen(true)}
              passed={progress.completedSkills[skill.id] === "quiz" || progress.quizScores[skill.id] === 100}
              percent={progress.quizScores[skill.id]}
            />
          ) : null}
          <StartingTeacherRecommended
          achievedTasks={progress.achievedTasks[skill.id]}
          note={note}
          onNote={setNote}
          onOpenChat={() => {
            const topicTitle = t(`startingTeacher.topics.${topicId}.title`);
            navigate(`/me/chat?focusTopic=${encodeURIComponent(topicTitle)}`);
          }}
          onOpenSources={() => setSourcesOpen(true)}
          onOpenTask={() => setTaskOpen(true)}
          onSaveNote={() => onSaveNote(note)}
          onStartTask={onAchieveTask}
          skill={skill}
          taskOpen={taskOpen}
          topicId={topicId}
        />
        </div>

        <StartingTeacherQuizModal
          onClose={() => setQuizOpen(false)}
          onResult={(result) => onQuizResult(result)}
          open={quizOpen}
          skillId={skill.id}
        />

        <Drawer
          closeLabel={t("common.close")}
          controlId="starting-teacher.sources"
          description={t("startingTeacher.sourcesBody")}
          onClose={() => setSourcesOpen(false)}
          open={sourcesOpen}
          side="right"
          size="wide"
          title={t("startingTeacher.sourcesTitle")}
        >
          <StartingTeacherSources sources={startingTeacherSources(skill.id)} />
        </Drawer>
      </StartingTeacherRouteFrame>
  );
}
